/**
 * ProjectsContext — the single source of truth for the company's project
 * list. Before this existed, every page + every ProjectSelector + the
 * sidebar switcher + half the form dialogs fetched /projects on their
 * own mount, often with different query params, producing subtly
 * inconsistent views (some included archived, some didn't, some filtered
 * by status, etc.).
 *
 * Mount <ProjectsProvider> once inside the auth gate in App.jsx. Anyone
 * who needs the list calls `useProjects()` and gets the same reference
 * every render until someone explicitly `refresh()`es (e.g. after a
 * create / archive action).
 *
 * Canonical filter: `deleted_at IS NULL` — soft-deleted projects are
 * hidden from all consumers. Archived projects (status='on_hold' or
 * 'completed') are still included — pages can filter further by status
 * locally, but this context always returns the full active list.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react"
import { projects as projectsApi, Project, getToken } from "@/lib/api"

interface ProjectsContextValue {
  projects: Project[]
  /** O(1) lookup by public UUID — the id the sidebar + ProjectSelector hand around. */
  byPublicId: Map<string, Project>
  /** O(1) lookup by the bigint internal id (`.project_id` on most doc rows). */
  byInternalId: Map<number, Project>
  loading: boolean
  error: string | null
  /** Refetch. Call after a create/archive/rename so every consumer sees fresh data. */
  refresh: () => Promise<void>
}

const ProjectsContext = createContext<ProjectsContextValue>({
  projects: [],
  byPublicId: new Map(),
  byInternalId: new Map(),
  loading: false,
  error: null,
  refresh: async () => {},
})

export function ProjectsProvider({ children }: { children: ReactNode }) {
  const [projects, setProjectsState] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      // limit=500 matches the previous ProjectSelector ceiling. If any
      // tenant outgrows that, we'll paginate — but that's a server-side
      // concern, not a reason for every caller to cap-and-hope.
      const res = await projectsApi.list({ limit: 500 })
      setProjectsState(res.data || [])
      setError(null)
    } catch (e: any) {
      setError(e?.message || "Failed to load projects")
      setProjectsState([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    // Only fetch when we actually have an auth token. Mounting the
    // provider on the login page would fire a 401.
    if (!getToken()) {
      setLoading(false)
      return
    }
    refresh()
  }, [refresh])

  const value = useMemo<ProjectsContextValue>(() => {
    const byPublicId = new Map<string, Project>()
    const byInternalId = new Map<number, Project>()
    for (const p of projects) {
      if (p.id) byPublicId.set(String(p.id), p)
      // Backend Project payload carries `internal_id` (bigint); every doc
      // row stores project_id as that same bigint. Populating this map
      // means the common UI pattern `projectsList.find(p => p.id === doc.project_id)`
      // (which silently failed because UUID !== bigint) can be replaced
      // with `useResolveProject(doc.project_id)` for a name.
      const internalId = (p as any).internal_id
      if (typeof internalId === "number") byInternalId.set(internalId, p)
    }
    return { projects, byPublicId, byInternalId, loading, error, refresh }
  }, [projects, loading, error, refresh])

  return <ProjectsContext.Provider value={value}>{children}</ProjectsContext.Provider>
}

export function useProjects(): ProjectsContextValue {
  return useContext(ProjectsContext)
}

/**
 * Handy resolver: given either a public UUID or bigint, return the
 * matching Project if it's in the cached list. Returns undefined if
 * neither form matches — e.g. the project was deleted or belongs to
 * another company. Callers shouldn't crash on undefined.
 */
/**
 * Shorthand for "what name should I display next to this doc row?".
 * Accepts either a bigint project_id (common on doc payloads) or the
 * public UUID string. Returns "—" when the id doesn't resolve, so
 * callers never have to null-check.
 */
export function useProjectName(id: string | number | null | undefined): string {
  const p = useResolveProject(id)
  return p?.name || "—"
}

export function useResolveProject(id: string | number | null | undefined): Project | undefined {
  const { byPublicId, byInternalId } = useProjects()
  if (id == null) return undefined
  if (typeof id === "number") return byInternalId.get(id)
  // String form: UUID or numeric-as-string. Try UUID first (the common path).
  const viaUuid = byPublicId.get(id)
  if (viaUuid) return viaUuid
  const asNum = Number(id)
  if (Number.isFinite(asNum)) return byInternalId.get(asNum)
  return undefined
}
